import { useCallback, useEffect, useState } from 'react'

import { useAppSelector } from 'reduxTyped'
import { getUserId } from 'common/core/selectors/appSelectors'
import { getInfoAboutService, initializeUsercentricsListeners } from 'utils/getInfoAboutService'

import { toIntercomSettings } from './intercomFinAISettings'
import { useIntercomFinAISettingsRefresh } from './useIntercomFinAISettingsRefresh'

const SCRIPT_ID = 'intercomFinAIAgent'
const USERCENTRICS_INTERCOM_NAME = 'Intercom'
const INTERCOM_DOM_SELECTORS = [
  '#intercom-container',
  '#intercom-frame',
  '.intercom-lightweight-app',
]

const getHasIntercomConsent = () =>
  getInfoAboutService(USERCENTRICS_INTERCOM_NAME)[USERCENTRICS_INTERCOM_NAME]?.status ?? false

const useIntercomUserConsent = () => {
  const [isProvided, setIsProvided] = useState(getHasIntercomConsent)

  useEffect(() => {
    const updateConsent = () => setIsProvided(getHasIntercomConsent())
    const { unsubscribeListeners } = initializeUsercentricsListeners(updateConsent)
    updateConsent()
    return unsubscribeListeners
  }, [])

  return isProvided
}

export const useIsIntercomFinAIEnabled = () => {
  const hasIntercomConsent = useIntercomUserConsent()
  const isLoggedIn = useAppSelector((state) => getUserId(state) !== null)

  return isLoggedIn && hasIntercomConsent
}

type IntercomQueueStub = NonNullable<Window['Intercom']> & {
  q: IArguments[]
  c: (args: IArguments) => void
}

const createIntercomQueueStub = () => {
  const stub = function () {
    stub.c(arguments)
  } as unknown as IntercomQueueStub

  stub.q = []
  stub.c = (args: IArguments) => {
    stub.q.push(args)
  }

  return stub
}

export const shutdownIntercomFinAIAgent = () => {
  try {
    window.Intercom?.('shutdown')
  } catch (error) {
    console.error('Failed to shut down Intercom', error)
  }

  delete window.Intercom

  document.getElementById(SCRIPT_ID)?.remove()
  INTERCOM_DOM_SELECTORS.forEach((selector) => {
    document.querySelectorAll(selector).forEach((element) => element.remove())
  })
}

export const useIntercomFinAIAgentWidget = () => {
  const isIntercomFinAIEnabled = useIsIntercomFinAIEnabled()
  const [isIntercomLoaded, setIsIntercomLoaded] = useState(false)

  const loadScript = useCallback(() => {
    const settings = window.intercomFinAISettings
    if (!settings?.app_id) {
      return
    }

    if (document.getElementById(SCRIPT_ID)) {
      return
    }

    window.Intercom = createIntercomQueueStub()
    window.Intercom('boot', toIntercomSettings(settings))

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.id = SCRIPT_ID
    script.src = `https://widget.intercom.io/widget/${settings.app_id}`

    script.onload = () => {
      setIsIntercomLoaded(true)
    }

    script.onerror = () => {
      setIsIntercomLoaded(false)
      console.error('Failed to load Intercom script')
    }

    document.head.appendChild(script)
  }, [])

  const updateIntercom = useCallback(() => {
    const intercom = window.Intercom
    const settings = window.intercomFinAISettings
    if (typeof intercom !== 'function' || !settings) {
      return false
    }

    intercom('reattach_activator')
    intercom('update', toIntercomSettings(settings))
    setIsIntercomLoaded(true)

    return true
  }, [])

  const loadIntercomFinAIAgent = useCallback(() => {
    if (updateIntercom()) {
      return
    }
    loadScript()
  }, [loadScript, updateIntercom])

  useEffect(() => {
    if (!isIntercomFinAIEnabled) {
      return
    }

    const onLoad = () => loadIntercomFinAIAgent()

    if (document.readyState === 'complete') {
      loadIntercomFinAIAgent()
    } else {
      window.addEventListener('load', onLoad, { once: true })
    }

    return () => {
      window.removeEventListener('load', onLoad)
      shutdownIntercomFinAIAgent()
      setIsIntercomLoaded(false)
    }
  }, [isIntercomFinAIEnabled, loadIntercomFinAIAgent])

  const handleFinDisabled = useCallback(() => {
    shutdownIntercomFinAIAgent()
    setIsIntercomLoaded(false)
  }, [])

  useIntercomFinAISettingsRefresh({
    isActive: isIntercomFinAIEnabled && isIntercomLoaded,
    onDisabled: handleFinDisabled,
  })

  return {
    isIntercomFinAIEnabled,
    isIntercomLoaded,
  }
}
